<script lang="ts">
  /**
   * Member/author picker — now a thin adapter over the shared `@ui/SearchSelect`, which
   * holds the typeahead, the chip and the click-outside. This file keeps only what is
   * member-specific: the roster search, the id back-fill, and the number↔string id
   * bridge (a member id is a number; SearchSelect speaks string ids).
   *
   * BOTH CALLS ARE ON `/v1/communities/{id}/members[/{member_id}]`, each asking for the
   * cheapest field set: the typeahead uses the default set (no `?include=activity`), the
   * back-fill uses `fetchMember` (three identity fields for a chip), not the dense card.
   * `staff` defaults to `exclude`, as `/admin/members` did.
   */
  import SearchSelect, { type SearchOption } from '@ui/SearchSelect.svelte';
  import { fetchMembers, fetchMember } from '@api';

  let {
    skoolId,
    value = $bindable<number | null>(null),
    onchange,
    placeholder = 'Any author',
  }: {
    skoolId: number | null | undefined;
    value?: number | null;
    onchange?: (id: number | null) => void;
    placeholder?: string;
  } = $props();

  async function search(q: string): Promise<SearchOption[]> {
    if (skoolId == null) return [];
    const r = await fetchMembers(skoolId, { q, sort: 'activity', order: 'desc', limit: 8 });
    return r.items.map((m) => ({
      id: String(m.member_id),
      name: m.name,
      avatarUrl: m.picture_profile,
      subtitle: `@${m.handle ?? 'unknown'} · ${m.stats?.posts ?? 0} posts`,
      ringId: m.member_id,
    }));
  }

  async function resolve(id: string): Promise<SearchOption | null> {
    if (skoolId == null) return null;
    try {
      const { member } = await fetchMember(skoolId, Number(id));
      return { id: String(member.member_id), name: member.name,
               avatarUrl: member.picture_profile, ringId: member.member_id };
    } catch {
      return null;
    }
  }
</script>

<SearchSelect
  value={value != null ? String(value) : null}
  {search}
  {resolve}
  {placeholder}
  searchPlaceholder="Search members…"
  emptyText="No members match."
  onchange={(opt) => { value = opt ? Number(opt.id) : null; onchange?.(value); }}
/>
